import { HexTile } from './hex/HexTile';
import { HexBoard } from './hex/HexBoard';
import { TileType } from './hex/HexTypes';

/**
 * ルート探索結果
 */
export interface RouteResult {
  route: HexTile[];
  marbles: MarbleSequence[];
  success: boolean;
  hiddenMarbles?: HiddenMarble[];
  crossesCenter?: boolean;
  requiresCenter?: boolean;
}

/**
 * タイル内での丸の使用順序
 */
export interface MarbleSequence {
  tile: HexTile;
  sequence: string[];
}

/**
 * 隠された丸の情報
 */
export interface HiddenMarble {
  tile: HexTile;
  marbleIndex: number;
}

/**
 * 探索状態
 */
interface SearchState {
  currentTile: HexTile;  // 現在のタイル
  patternIndex: number;  // パターンの現在位置
  visitedTiles: Set<string>;  // 訪問済みタイル
  route: HexTile[];  // ルート
  marbles: MarbleSequence[];  // 使用した丸のシーケンス
  greenChipsUsed: HiddenMarble[];  // 使用した緑チップ
  crossesCenter: boolean;  // 中央またぎフラグ
}

/**
 * 優先度付き探索用の状態
 */
interface PriorityState extends SearchState {
  priority: number;
}

/**
 * チムチムドンドンチムドンドンのルート探索クラス V4
 * ゲームバランス最適化版：タイル数のバリエーションを重視
 */
export class RouteSearcherV4 {
  private readonly TARGET_PATTERN = ['RED', 'RED', 'BLUE', 'BLUE', 'RED', 'BLUE', 'BLUE'];
  private board: HexBoard;
  private newlyPlacedTile: HexTile | null = null;
  private flagshipTile: HexTile | null = null;
  private foundRoutes: RouteResult[] = [];
  private maxGreenChips: number = 2;  // 使用可能な緑チップの最大数
  
  // ゲームバランス用：緑チップ数×タイル数の組み合わせを記録
  private foundCombinations: Map<string, RouteResult[]> = new Map();
  // ルート重複チェック用：ルート署名を記録
  private foundRouteSignatures: Set<string> = new Set();
  
  constructor(board: HexBoard) {
    this.board = board;
  }
  
  /**
   * 使用可能な緑チップの最大数を設定
   */
  public setMaxGreenChips(max: number): void {
    this.maxGreenChips = Math.min(Math.max(0, max), 2);  // 0-2の範囲に制限
  }
  
  /**
   * 全てのルートを探索（ゲームバランス最適化版）
   */
  public findAllRoutes(): RouteResult[] {
    this.foundRoutes = [];
    this.foundCombinations.clear();
    this.foundRouteSignatures.clear();
    
    // 新規配置タイルを見つける
    this.newlyPlacedTile = this.findNewlyPlacedTile();
    if (!this.newlyPlacedTile) {
      console.log('No newly placed tile found');
      return [];
    }
    
    // 旗頭タイルを取得
    this.flagshipTile = this.board.getFlagshipTile();
    
    // 星マークタイルの確認
    const hasStarTile = this.board.hasStarTileInCurrentRound();
    
    console.log(`Newly placed tile: (${this.newlyPlacedTile.coords.x},${this.newlyPlacedTile.coords.y},${this.newlyPlacedTile.coords.z}) - ${this.newlyPlacedTile.getTileType()}`);
    if (this.flagshipTile) {
      console.log(`Flagship tile: (${this.flagshipTile.coords.x},${this.flagshipTile.coords.y},${this.flagshipTile.coords.z})`);
    }
    if (hasStarTile) {
      console.log('Star tile detected - only center-crossing routes will be valid');
    }
    
    // 全ルートを保存（重複含む）
    const allRoutes: RouteResult[] = [];
    
    // 緑チップ数ごとに探索（0→maxGreenChipsの順）
    for (let greenCount = 0; greenCount <= this.maxGreenChips; greenCount++) {
      console.log(`\nSearching with ${greenCount} green chips...`);
      this.searchWithGreenChips(greenCount);
      
      // この緑チップ数で見つかった組み合わせを表示
      const foundInThisRound = Array.from(this.foundCombinations.entries())
        .filter(([key]) => key.startsWith(`${greenCount}-`))
        .map(([key]) => {
          const tileCount = parseInt(key.split('-')[1]);
          return tileCount;
        })
        .sort((a, b) => a - b);
      
      if (foundInThisRound.length > 0) {
        console.log(`  Found tile counts: ${foundInThisRound.join(', ')}`);
      }
    }
    
    // 結果を収集（配列をフラット化）
    this.foundRoutes = [];
    for (const routes of this.foundCombinations.values()) {
      this.foundRoutes.push(...routes);
    }
    
    // 各枚数・緑チップ数の組み合わせでルート数を集計
    const routeStats = new Map<string, number>();
    this.foundRoutes.forEach(route => {
      const key = `${route.route.length}枚/${route.hiddenMarbles?.length || 0}緑`;
      routeStats.set(key, (routeStats.get(key) || 0) + 1);
    });
    
    // 統計情報を表示
    console.log('\n=== ルート統計 ===');
    const sortedStats = Array.from(routeStats.entries())
      .sort((a, b) => {
        // 枚数でソート、次に緑チップ数でソート
        const [aTiles, aGreen] = a[0].split('/').map(s => parseInt(s));
        const [bTiles, bGreen] = b[0].split('/').map(s => parseInt(s));
        if (aTiles !== bTiles) return aTiles - bTiles;
        return aGreen - bGreen;
      });
    
    sortedStats.forEach(([key, count]) => {
      console.log(`  ${key}: ${count}通り`);
    });
    
    console.log(`  合計: ${this.foundRoutes.length}通り`);
    
    // ソートして返す
    return this.sortRoutes(this.foundRoutes);
  }
  
  /**
   * 指定された緑チップ数でルートを探索
   */
  private searchWithGreenChips(maxGreenChips: number): void {
    // 星マークタイルの確認（早期判定）
    const hasStarTile = this.board.hasStarTileInCurrentRound();
    
    // 緑チップの配置パターンを生成
    const greenPatterns = this.generateGreenPatterns(maxGreenChips);
    
    for (const greenPattern of greenPatterns) {
      // 既に十分なバリエーションが見つかっているかチェック
      if (this.hasEnoughVariations(maxGreenChips)) {
        console.log(`  Enough variations found for ${maxGreenChips} green chips`);
        break;
      }
      
      // この緑チップパターンで探索（星マークフラグを渡す）
      this.searchWithGreenPattern(greenPattern, hasStarTile);
    }
  }
  
  /**
   * 特定の緑チップパターンでルートを探索
   */
  private searchWithGreenPattern(greenChips: HiddenMarble[], hasStarTile: boolean = false): void {
    const greenCount = greenChips.length;
    
    // 優先度付きキュー（より少ないタイル数を優先）
    const queue: PriorityState[] = [];
    const visited = new Map<string, number>(); // state key -> best tile count
    
    // 全てのタイルから開始を試みる
    const allTiles = this.board.getAllTiles();
    for (const startTile of allTiles) {
      if (this.isEmptyOrCenter(startTile)) continue;
      
      const initialState = this.createInitialState(startTile, greenChips);
      if (initialState) {
        queue.push(initialState);
      }
    }
    
    // 優先度順にソート
    queue.sort((a, b) => a.priority - b.priority);
    
    // BFS探索
    while (queue.length > 0) {
      const state = queue.shift()!;
      
      // 訪問済みチェック（メモ化）
      const stateKey = this.getStateKey(state);
      const currentTileCount = state.route.length;
      if (visited.has(stateKey)) {
        const bestTileCount = visited.get(stateKey)!;
        if (currentTileCount >= bestTileCount) continue;
      }
      visited.set(stateKey, currentTileCount);
      
      // パターン完成チェック
      if (state.patternIndex === this.TARGET_PATTERN.length) {
        // 必須タイルを通っているか確認
        if (!state.visitedTiles.has(this.newlyPlacedTile!.id)) continue;
        
        // 星マークタイルがある場合、中央またぎルートのみ有効
        if (hasStarTile && !state.crossesCenter) continue;
        
        // ルート署名を生成（重複チェック用）
        const routeSignature = this.getRouteSignature(state.route);
        if (this.foundRouteSignatures.has(routeSignature)) continue;
        
        // より少ない緑チップで同じタイル数のルートが既にある場合はスキップ
        let shouldAdd = true;
        for (let g = 0; g < greenCount; g++) {
          const existingKey = `${g}-${state.route.length}`;
          if (this.foundCombinations.has(existingKey) && this.foundCombinations.get(existingKey)!.length > 0) {
            shouldAdd = false;  // より少ない緑チップで同じタイル数のルートが既にある
            break;
          }
        }
        
        if (!shouldAdd) continue;
        
        // 同じタイル数で緑チップ数が多いルートがある場合は削除
        for (let g = greenCount + 1; g <= 2; g++) {
          const replaceKey = `${g}-${state.route.length}`;
          if (this.foundCombinations.has(replaceKey)) {
            this.foundCombinations.delete(replaceKey);
            console.log(`  Replaced: ${state.route.length} tiles with ${g} green chips → ${greenCount} green chips`);
          }
        }
        
        // この組み合わせ（緑チップ数-タイル数）を記録
        const combinationKey = `${greenCount}-${state.route.length}`;
        
        // 新しいルートを記録
        this.foundRouteSignatures.add(routeSignature);
        
        const result: RouteResult = {
          route: state.route,
          marbles: state.marbles,
          success: true,
          hiddenMarbles: state.greenChipsUsed,
          crossesCenter: state.crossesCenter
        };
        
        // 配列に追加
        if (!this.foundCombinations.has(combinationKey)) {
          this.foundCombinations.set(combinationKey, []);
        }
        this.foundCombinations.get(combinationKey)!.push(result);
        
        const currentCount = this.foundCombinations.get(combinationKey)!.length;
        console.log(`  Found: ${state.route.length} tiles with ${greenCount} green chips${state.crossesCenter ? ' [crosses center]' : ''} (${currentCount}個目)`);
        
        // 続けて他のルートも探索
        continue;
      }
      
      // 次の状態を生成
      const nextStates = this.generateNextStates(state, greenChips);
      
      // 枝刈り：既に見つかった組み合わせになる可能性がない経路をスキップ
      for (const nextState of nextStates) {
        // 星マークタイルがある場合の早期枝刈り
        // 中央またぎの可能性がない経路は探索しない
        if (hasStarTile && !nextState.crossesCenter) {
          // 残りのステップで中央またぎする可能性があるかチェック
          const canCrossCenter = this.canReachCenterCrossing(nextState, this.TARGET_PATTERN.length - nextState.patternIndex);
          if (!canCrossCenter) continue;
        }
        
        const minTiles = nextState.route.length + Math.ceil((this.TARGET_PATTERN.length - nextState.patternIndex) / 3);
        const maxTiles = nextState.route.length + (this.TARGET_PATTERN.length - nextState.patternIndex);
        
        // この範囲に未発見の組み合わせがあるか
        let hasNewCombination = false;
        for (let tiles = minTiles; tiles <= maxTiles; tiles++) {
          const key = `${greenCount}-${tiles}`;
          if (!this.foundCombinations.has(key)) {
            hasNewCombination = true;
            break;
          }
        }
        
        if (hasNewCombination) {
          // 優先度を計算して追加
          nextState.priority = this.calculatePriority(nextState, greenCount);
          
          // 適切な位置に挿入（優先度順を維持）
          const insertIndex = queue.findIndex(s => s.priority > nextState.priority);
          if (insertIndex === -1) {
            queue.push(nextState);
          } else {
            queue.splice(insertIndex, 0, nextState);
          }
        }
      }
    }
  }
  
  /**
   * 初期状態を作成
   */
  private createInitialState(startTile: HexTile, greenChips: HiddenMarble[]): PriorityState | null {
    // 空の初期状態（generateMarbleSequencesで使用）
    const emptyState: SearchState = {
      currentTile: startTile,
      patternIndex: 0,
      visitedTiles: new Set<string>(),
      route: [],
      marbles: [],
      greenChipsUsed: [],
      crossesCenter: false
    };
    
    // このタイルで使用可能な丸のシーケンスを生成
    const sequences = this.generateMarbleSequences(startTile, greenChips, emptyState);
    
    // パターンにマッチするシーケンスを探す
    for (const sequence of sequences) {
      if (this.matchesPattern(0, sequence)) {
        // 使用する緑チップを収集
        const usedGreenChips: HiddenMarble[] = [];
        const tileColors = this.getTileColors(startTile);
        
        // 緑チップで隠された丸を特定
        for (let i = 0; i < tileColors.length; i++) {
          const green = greenChips.find(g => g.tile.id === startTile.id && g.marbleIndex === i);
          if (green) {
            usedGreenChips.push(green);
          }
        }
        
        return {
          currentTile: startTile,
          patternIndex: sequence.length,
          visitedTiles: new Set([startTile.id]),
          route: [startTile],
          marbles: [{
            tile: startTile,
            sequence: sequence
          }],
          greenChipsUsed: usedGreenChips,
          crossesCenter: false,
          priority: 0
        };
      }
    }
    
    return null;
  }
  
  /**
   * 次の状態を生成
   */
  private generateNextStates(state: SearchState, greenChips: HiddenMarble[]): PriorityState[] {
    const nextStates: PriorityState[] = [];
    
    // 隣接タイルへ移動（中央またぎ含む）
    const neighbors = state.currentTile.getNeighbors();
    for (const neighbor of neighbors) {
      if (this.isEmptyOrCenter(neighbor)) continue;
      if (state.visitedTiles.has(neighbor.id)) continue;  // 訪問済みはスキップ
      
      // 中央またぎチェック
      const distance = state.currentTile.distanceTo(neighbor);
      const crossesCenter = distance > 1;
      
      // このタイルで使用可能な丸のシーケンスを生成
      const sequences = this.generateMarbleSequences(neighbor, greenChips, state);
      
      for (const sequence of sequences) {
        // シーケンスがパターンにマッチするか確認
        if (this.matchesPattern(state.patternIndex, sequence)) {
          const nextState = this.createNextState(state, neighbor, sequence, greenChips, crossesCenter);
          if (nextState) {
            nextStates.push(nextState as PriorityState);
          }
        }
      }
    }
    
    return nextStates;
  }
  
  /**
   * タイルで使用可能な丸のシーケンスを生成
   */
  private generateMarbleSequences(tile: HexTile, greenChips: HiddenMarble[], state: SearchState): string[][] {
    const sequences: string[][] = [];
    
    // 旗頭タイルの特別処理
    if (this.flagshipTile && tile === this.flagshipTile) {
      // 旗頭タイルはREDまたはBLUEを1個だけ
      sequences.push(['RED']);
      sequences.push(['BLUE']);
      return sequences;
    }
    
    // タイルの丸を取得
    const tileColors = this.getTileColors(tile);
    
    // 緑チップで隠されていない丸を取得
    const visibleColors: string[] = [];
    for (let i = 0; i < tileColors.length; i++) {
      const isHidden = greenChips.some(g => g.tile.id === tile.id && g.marbleIndex === i);
      if (!isHidden) {
        visibleColors.push(tileColors[i]);
      }
    }
    
    // タイルに1個しか丸がない場合、緑チップで隠せない
    if (tileColors.length === 1 && visibleColors.length === 0) {
      return [];  // 隠せない
    }
    
    // 使用可能な丸がない場合
    if (visibleColors.length === 0) {
      return [];
    }
    
    // 全ての順列を生成
    return this.generatePermutations(visibleColors);
  }
  
  /**
   * 配列の全順列を生成
   */
  private generatePermutations(arr: string[]): string[][] {
    if (arr.length <= 1) return [arr];
    
    const result: string[][] = [];
    for (let i = 0; i < arr.length; i++) {
      const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
      const perms = this.generatePermutations(rest);
      for (const perm of perms) {
        result.push([arr[i], ...perm]);
      }
    }
    return result;
  }
  
  /**
   * シーケンスがパターンにマッチするか確認
   */
  private matchesPattern(patternIndex: number, sequence: string[]): boolean {
    if (patternIndex + sequence.length > this.TARGET_PATTERN.length) {
      return false;
    }
    
    for (let i = 0; i < sequence.length; i++) {
      if (this.TARGET_PATTERN[patternIndex + i] !== sequence[i]) {
        return false;
      }
    }
    return true;
  }
  
  /**
   * 次の状態を作成
   */
  private createNextState(state: SearchState, tile: HexTile, sequence: string[], greenChips: HiddenMarble[], crossesCenter: boolean): SearchState | null {
    // このタイルで使用する緑チップを収集
    const usedGreenChips: HiddenMarble[] = [];
    const tileColors = this.getTileColors(tile);
    
    for (let i = 0; i < tileColors.length; i++) {
      const green = greenChips.find(g => g.tile.id === tile.id && g.marbleIndex === i);
      if (green && !state.greenChipsUsed.includes(green)) {
        usedGreenChips.push(green);
      }
    }
    
    const newState: SearchState = {
      currentTile: tile,
      patternIndex: state.patternIndex + sequence.length,
      visitedTiles: new Set(state.visitedTiles),
      route: [...state.route],
      marbles: [...state.marbles],
      greenChipsUsed: [...state.greenChipsUsed, ...usedGreenChips],
      crossesCenter: state.crossesCenter || crossesCenter
    };
    
    // 新しいタイルを訪問
    newState.visitedTiles.add(tile.id);
    newState.route.push(tile);
    
    // マーブルシーケンスを追加
    newState.marbles.push({ tile, sequence });
    
    return newState;
  }
  
  /**
   * 優先度を計算
   */
  private calculatePriority(state: SearchState, greenCount: number): number {
    // 基本優先度：タイル数が少ないほど優先
    let priority = state.route.length * 1000;
    
    // パターン進捗ボーナス
    priority -= state.patternIndex * 10;
    
    // 必須タイル通過ボーナス
    if (!state.visitedTiles.has(this.newlyPlacedTile!.id)) {
      priority += 500; // ペナルティ
    }
    
    // この組み合わせが既に見つかっている場合は低優先度
    const combinationKey = `${greenCount}-${state.route.length}`;
    if (this.foundCombinations.has(combinationKey)) {
      priority += 10000;
    }
    
    return priority;
  }
  
  /**
   * 状態のキーを生成（メモ化用）
   */
  private getStateKey(state: SearchState): string {
    const tiles = Array.from(state.visitedTiles).sort().join(',');
    return `${state.currentTile.id}-${state.patternIndex}-${tiles}`;
  }
  
  /**
   * 十分なバリエーションが見つかったかチェック
   */
  private hasEnoughVariations(greenCount: number): boolean {
    // 早期終了を無効化 - 全てのパターンを探索
    return false;
  }
  
  /**
   * ルート署名を生成（重複チェック用）
   */
  private getRouteSignature(route: HexTile[]): string {
    // タイルIDを座標順でソートして署名を作成
    const sortedIds = route
      .map(t => t.id)
      .sort()
      .join(',');
    return sortedIds;
  }
  
  /**
   * 中央またぎに到達可能かチェック
   */
  private canReachCenterCrossing(state: SearchState, remainingSteps: number): boolean {
    // 既に中央またぎしている
    if (state.crossesCenter) return true;
    
    // 残りステップ数が少ない場合は詳細チェック不要
    if (remainingSteps <= 1) return false;
    
    // 現在位置から中央に隣接するタイルまでの距離を考慮
    const currentCoords = state.currentTile.coords;
    const distanceToCenter = Math.abs(currentCoords.x) + Math.abs(currentCoords.y) + Math.abs(currentCoords.z);
    
    // 中央から離れすぎている場合は到達不可能
    if (distanceToCenter > remainingSteps + 1) return false;
    
    return true;
  }
  
  /**
   * 緑チップの配置パターンを生成
   */
  private generateGreenPatterns(count: number): HiddenMarble[][] {
    if (count === 0) return [[]];
    
    const patterns: HiddenMarble[][] = [];
    const allTiles = this.board.getAllTiles();
    
    if (count === 1) {
      // 1個の緑チップ
      for (const tile of allTiles) {
        if (this.isEmptyOrCenter(tile)) continue;
        const colors = this.getTileColors(tile);
        for (let i = 0; i < colors.length; i++) {
          patterns.push([{ tile, marbleIndex: i }]);
        }
      }
    } else if (count === 2) {
      // 2個の緑チップ（組み合わせを制限）
      // 新規配置タイル周辺のみに限定して計算量を削減
      const nearbyTiles = [this.newlyPlacedTile!];
      for (const neighbor of this.newlyPlacedTile!.getNeighbors()) {
        if (!this.isEmptyOrCenter(neighbor)) {
          nearbyTiles.push(neighbor);
        }
      }
      
      for (let i = 0; i < nearbyTiles.length; i++) {
        const tile1 = nearbyTiles[i];
        const colors1 = this.getTileColors(tile1);
        
        // 同じタイルから2個
        if (colors1.length >= 2) {
          for (let j = 0; j < colors1.length; j++) {
            for (let k = j + 1; k < colors1.length; k++) {
              patterns.push([
                { tile: tile1, marbleIndex: j },
                { tile: tile1, marbleIndex: k }
              ]);
            }
          }
        }
        
        // 異なるタイルから1個ずつ（全ての組み合わせ）
        for (let j = i + 1; j < nearbyTiles.length; j++) {
          const tile2 = nearbyTiles[j];
          const colors2 = this.getTileColors(tile2);
          
          // 各タイルの全インデックスの組み合わせを生成
          for (let idx1 = 0; idx1 < colors1.length; idx1++) {
            for (let idx2 = 0; idx2 < colors2.length; idx2++) {
              patterns.push([
                { tile: tile1, marbleIndex: idx1 },
                { tile: tile2, marbleIndex: idx2 }
              ]);
            }
          }
        }
      }
    }
    
    return patterns;
  }
  
  /**
   * タイルの色を取得
   */
  private getTileColors(tile: HexTile): string[] {
    const tileType = tile.hasStar && tile.hasStar() ? tile.getBaseTileType() : tile.getTileType();
    
    switch (tileType) {
      case TileType.BLUE_1:
        return ['BLUE'];
      case TileType.RED_1:
        return ['RED'];
      case TileType.BLUE_2:
        return ['BLUE', 'BLUE'];
      case TileType.RED_2:
        return ['RED', 'RED'];
      case TileType.BLUE_1_RED_1:
        return ['BLUE', 'RED'];
      case TileType.BLUE_2_RED_1:
        return ['BLUE', 'BLUE', 'RED'];
      case TileType.BLUE_1_RED_2:
        return ['BLUE', 'RED', 'RED'];
      default:
        return [];
    }
  }
  
  /**
   * 利用可能な色を取得（緑チップ考慮）
   */
  private getAvailableColors(tile: HexTile, greenChips: HiddenMarble[]): string[] {
    const colors = this.getTileColors(tile);
    const available: string[] = [];
    
    for (let i = 0; i < colors.length; i++) {
      const isHidden = greenChips.some(g => g.tile.id === tile.id && g.marbleIndex === i);
      if (!isHidden) {
        available.push(colors[i]);
      }
    }
    
    // 緑チップで任意の色を追加できる
    const greenOnTile = greenChips.filter(g => g.tile.id === tile.id).length;
    for (let i = 0; i < greenOnTile; i++) {
      available.push('ANY'); // 任意の色を表す
    }
    
    return available;
  }
  
  /**
   * 新規配置タイルを見つける
   */
  private findNewlyPlacedTile(): HexTile | null {
    const allTiles = this.board.getAllTiles();
    return allTiles.find(tile => tile.isNewlyPlaced()) || null;
  }
  
  /**
   * 空タイルまたは中央タイルか判定
   */
  private isEmptyOrCenter(tile: HexTile): boolean {
    const coords = tile.coords;
    const isCenter = coords.x === 0 && coords.y === 0 && coords.z === 0;
    return tile.getTileType() === TileType.EMPTY || isCenter;
  }
  
  /**
   * ルートをソート
   */
  private sortRoutes(routes: RouteResult[]): RouteResult[] {
    return routes.sort((a, b) => {
      // まず緑チップ数でソート
      const aGreen = a.hiddenMarbles?.length || 0;
      const bGreen = b.hiddenMarbles?.length || 0;
      if (aGreen !== bGreen) return aGreen - bGreen;
      
      // 次にタイル数でソート
      return a.route.length - b.route.length;
    });
  }
  
  /**
   * 見つかった全ルートを取得
   */
  public getFoundRoutes(): RouteResult[] {
    return this.foundRoutes;
  }
}